import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ListingResource,
  useFieldHidden,
  useFormGroups,
  useListingActions,
  useListingField,
  useListingMetadata,
  useListingStore,
  useHighlightedFields,
  GroupProgress,
  SectionProgress,
} from '@brightmls/nextgen.lae.flc';
import { useMetadata } from '../../../../contexts/MetadataContext';
import { PropertyField } from '../../../../system/field';
import { Layout, NavigationGroup, NavigationLogicResult } from '../types';
import { useScrollToElement } from './useScrollToElement';
import { useHeaderHeight } from './useHeaderHeight';
import { navigateTo } from '../../../../utils';
import {
  BADGE_ERROR_HIGHLIGHT_TIME,
  KEYSTONE_PREVIEW_URL,
} from '../../../../config';
import { useModal } from '../../../../contexts/ModalContext';
import { useEditListing } from '../../../../contexts/EditListingContext';
import { focusField } from '../../../../utils/dom';
import { isResidentalListing } from '../../../../system/property-type';
import { useShareDraft } from '../../hooks/useShareDraft/useShareDraft';
import { useAuth } from '../../../../contexts/AuthContext';
import { toSectionSlug, findScrollTarget } from '../../../../utils/sectionSlug';
import { useRoomsSimulatedValidation } from '../../control/LevelsAndRooms/useSimulatedValidation';

const SCROLL_PADDING = 100;

export const useNavigationLogic = (): NavigationLogicResult => {
  const { template, field } = useMetadata();
  const { isViewOnly } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [value, setValue] = useState(0);
  const [isSectionsDrawerOpen, setIsSectionsDrawerOpen] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const { showModal } = useModal();
  const [searchTerm, setSearchTerm] = useState('');

  const { saveListing } = useListingActions();
  const { publish } = useEditListing();
  const { key } = useListingMetadata();
  const isFieldHiddenCheck = useFieldHidden();
  const { setFocusedFields } = useListingStore();
  const { addHighlightedField } = useHighlightedFields();
  const { onShareDraft } = useShareDraft();

  const propertyType = useListingField(
    ListingResource.Property,
    PropertyField.PROPERTY_TYPE,
    'Residential',
  );

  // RES, RESI, RESL -- match any property types targeting residental listings
  const isResidental = isResidentalListing(propertyType);

  const formGroups = useFormGroups();
  const headerHeight = useHeaderHeight();
  const [activeGroup, setActiveGroup] = useState('');
  const [activeSection, setActiveSection] = useState('');
  const [isProgrammaticScroll, setIsProgrammaticScroll] = useState(false);

  const scrollToElement = useScrollToElement({
    setIsProgrammaticScroll,
    headerHeight,
  });

  const groups = useMemo(() => {
    const templateFor = template(propertyType.value);

    return templateFor.groups
      .map(
        (group) =>
          ({
            name: group.name,
            sections: group.sections
              .filter((o) => !!o.name)
              .map((section) => ({
                name: section.name,
                fields: section.fields.map((item) => {
                  const fieldInfo = field(item.Key || '');
                  return {
                    key: item.Key ?? '',
                    label: item.Label || fieldInfo?.Name || '',
                    layout: item.Layout as Layout,
                    forcedRequired: item.ForceRequired,
                    definition: fieldInfo?.Definition,
                    lookupKey: fieldInfo?.Lookup,
                    control: item.Control,
                  };
                }),
              }))
              .filter((section) => {
                return section.fields.some((fieldItem) => {
                  const isHidden = isFieldHiddenCheck(
                    ListingResource.Property,
                    fieldItem.key,
                  );
                  return !isHidden;
                });
              }),
          }) as NavigationGroup,
      )
      .filter((group) => {
        return group.sections.length > 0;
      });
  }, [template, propertyType.value, field, isFieldHiddenCheck]);

  const roomsSimulated = useRoomsSimulatedValidation();

  // Locate the section/group that hosts the LevelsAndRooms control so we can
  // attach simulated ACCEPT-rule errors to its sidepanel badge.
  const { roomsSectionName, roomsGroupName } = useMemo(() => {
    for (const group of groups) {
      for (const section of group.sections) {
        if (
          section.fields.some(
            (f) =>
              f.control === 'LevelsAndRooms' ||
              f.control === 'LevelsAndRoomsNoMain',
          )
        ) {
          return { roomsSectionName: section.name, roomsGroupName: group.name };
        }
      }
    }
    return { roomsSectionName: null, roomsGroupName: null };
  }, [groups]);

  // Super efficient error counting using form progress!
  const {
    sectionErrors,
    groupErrors,
    sectionFirstErrorFieldKeyMap,
    groupFirstErrorFieldKeyMap,
  } = useMemo(() => {
    const sectionErrorMap: { [key: string]: number } = {};
    const groupErrorMap: { [key: string]: number } = {};
    const sectionFirstErrorFieldKeyMap: { [key: string]: string | null } = {};
    const groupFirstErrorFieldKeyMap: { [key: string]: string | null } = {};

    // Get errors from form progress system
    formGroups.forEach((group: GroupProgress) => {
      groupErrorMap[group.name] = group.actualErrorCount;
      groupFirstErrorFieldKeyMap[group.name] = group.firstErrorFieldKey;

      group.sections.forEach((section: SectionProgress) => {
        sectionErrorMap[section.name] = section.actualErrorCount;
        sectionFirstErrorFieldKeyMap[section.name] = section.firstErrorFieldKey;
      });
    });

    // Under NGW_ROOMS_USER_ENTERED_COUNTS the per-level bed/bath ACCEPT rules
    // never register with FLC (fields aren't rendered on the page), so we
    // overlay the simulated error count onto the rooms section + group.
    if (roomsSimulated.errorCount > 0 && roomsSectionName) {
      sectionErrorMap[roomsSectionName] =
        (sectionErrorMap[roomsSectionName] || 0) + roomsSimulated.errorCount;
      if (!sectionFirstErrorFieldKeyMap[roomsSectionName]) {
        sectionFirstErrorFieldKeyMap[roomsSectionName] =
          roomsSimulated.bedroomsMissing
            ? PropertyField.MAIN_LEVEL_BEDROOMS
            : PropertyField.MAIN_LEVEL_FULL_BATHS;
      }
      if (roomsGroupName) {
        groupErrorMap[roomsGroupName] =
          (groupErrorMap[roomsGroupName] || 0) + roomsSimulated.errorCount;
        if (!groupFirstErrorFieldKeyMap[roomsGroupName]) {
          groupFirstErrorFieldKeyMap[roomsGroupName] =
            sectionFirstErrorFieldKeyMap[roomsSectionName];
        }
      }
    }

    return {
      sectionErrors: sectionErrorMap,
      groupErrors: groupErrorMap,
      sectionFirstErrorFieldKeyMap,
      groupFirstErrorFieldKeyMap,
    };
  }, [
    formGroups,
    isFieldHiddenCheck,
    roomsSimulated.errorCount,
    roomsSimulated.bedroomsMissing,
    roomsSectionName,
    roomsGroupName,
  ]);

  const handleSearchEntriesChange = (results: PropertyField[] | null) => {
    setFocusedFields(results);
  };

  const handleGroupClick = useCallback(
    (
      groupName: string,
      fieldKey?: string | null,
      isMobile?: boolean,
      followErrorField?: boolean,
    ) => {
      setActiveGroup(groupName);

      const group = groups.find((g) => g.name === groupName);

      let problematicField = groupFirstErrorFieldKeyMap[groupName];
      if (
        !fieldKey &&
        followErrorField &&
        problematicField &&
        // Participants have a custom card for their required fields, fallback to natural scroll
        groupName !== 'Transaction Participants'
      ) {
        fieldKey = problematicField;

        focusField(problematicField as PropertyField);

        addHighlightedField(
          ListingResource.Property,
          problematicField,
          BADGE_ERROR_HIGHLIGHT_TIME,
        );
      }

      if (group && group.sections.length) {
        const section = fieldKey
          ? group.sections.find((s) => s.fields.some((f) => f.key === fieldKey))
          : group.sections[0];

        const sectionToActivate = section?.name;

        if (sectionToActivate) {
          scrollToElement(
            fieldKey || toSectionSlug(sectionToActivate),
            SCROLL_PADDING,
          );
          setActiveSection(sectionToActivate);
        }
      }
    },
    [
      groups,
      setActiveGroup,
      scrollToElement,
      setActiveSection,
      groupFirstErrorFieldKeyMap,
    ],
  );

  const handleSectionClick = useCallback(
    (sectionName: string, followError?: boolean) => {
      let problematicField = followError
        ? sectionFirstErrorFieldKeyMap[sectionName]
        : undefined;

      if (sectionName === 'Brokerage Participants') {
        // Participants have a custom card for their required fields, fallback to natural scroll
        problematicField = undefined;
      }

      if (problematicField && followError) {
        focusField(problematicField as PropertyField);

        addHighlightedField(
          ListingResource.Property,
          problematicField,
          BADGE_ERROR_HIGHLIGHT_TIME,
        );
      }

      scrollToElement(
        problematicField || toSectionSlug(sectionName),
        SCROLL_PADDING,
      );
      setActiveSection(sectionName);
    },
    [scrollToElement, setActiveSection, sectionFirstErrorFieldKeyMap],
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseDrawer = () => {
    setIsSectionsDrawerOpen(false);
  };

  const handleSectionsDrawerToggle = () => {
    if (!isSectionsDrawerOpen) {
      setIsSectionsDrawerOpen(true);
    } else if (!isSearchMode) {
      setIsSectionsDrawerOpen(false);
    }
    setIsSearchMode(false);
  };

  const handleSearchDrawerToggle = () => {
    if (!isSectionsDrawerOpen) {
      setIsSectionsDrawerOpen(true);
    } else if (isSearchMode) {
      setIsSectionsDrawerOpen(false);
    }
    setIsSearchMode(true);
  };

  const handleSearchClick = () => {
    setIsSearchMode(true);
    setIsSectionsDrawerOpen(true);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const filteredResults = useMemo(
    () =>
      groups.flatMap((group) =>
        group.sections.flatMap((section) =>
          section.fields
            .filter(
              (field) =>
                field?.definition?.Searchable &&
                !isFieldHiddenCheck(
                  ListingResource.Property,
                  field.key as PropertyField,
                ) &&
                field?.label?.toLowerCase().includes(searchTerm.toLowerCase()),
            )
            .map((field) => ({
              groupName: group.name,
              sectionName: section.name,
              fieldLabel: field.label,
              fieldKey: field.key,
            })),
        ),
      ),
    [groups, searchTerm, isFieldHiddenCheck],
  );

  // Scroll to the URL hash fragment once listing data is loaded
  const hasScrolledToHash = useRef(false);
  const scrollToElementRef = useRef(scrollToElement);
  useEffect(() => {
    scrollToElementRef.current = scrollToElement;
  }, [scrollToElement]);

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;

    const scrollToTarget = (targetId: string) => {
      scrollToElementRef.current(targetId, SCROLL_PADDING);
    };

    const doScroll = () => {
      const target = findScrollTarget(hash);
      if (target) {
        hasScrolledToHash.current = true;
        scrollToTarget(target.id);
        // Re-scroll after layout settles (images/dynamic content can shift position)
        setTimeout(() => scrollToTarget(target.id), 1000);
        return true;
      }
      return false;
    };

    const handleDataLoaded = () => {
      if (hasScrolledToHash.current) return;
      if (doScroll()) return;

      // Section not in DOM yet — observe until it appears
      const observer = new MutationObserver(() => {
        if (doScroll()) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 5000);
    };

    // Fresh load: wait for listing:data-loaded event from LoadedContent
    window.addEventListener('listing:data-loaded', handleDataLoaded, {
      once: true,
    });

    // Already-loaded case (e.g. navigating back, data in store): scroll immediately
    if (key && groups.length > 0) handleDataLoaded();

    return () => {
      window.removeEventListener('listing:data-loaded', handleDataLoaded);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePublish = () => {
    publish();
  };

  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    try {
      await saveListing();
    } catch (e) {
      console.error(e);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleShareDraft = () => {
    const errors = Object.values(sectionErrors).some((value) => value > 0);

    onShareDraft(errors);
  };

  const handlePreviewDraft = () => {
    const errors = Object.values(sectionErrors).some((value) => value > 0);
    if (errors) {
      showModal({
        content:
          'This listing has missing required fields and is not available to be previewed. Please return to the form and provide information to the missing required fields and try again.',
        cancelText: 'Edit Listing',
      });
      console.warn('Can not preview with errors on the form');
      return;
    }
    navigateTo(`${KEYSTONE_PREVIEW_URL}${key}`, true);
  };

  return {
    groups,
    activeGroup,
    activeSection,
    anchorEl,
    value,
    isSectionsDrawerOpen,
    isSearchMode,
    searchTerm,
    headerHeight,
    navHeight: `${window.innerHeight - headerHeight}px`, // backwards compatibility for ResponsiveDesktopNavigation
    sectionErrors,
    groupErrors,
    filteredResults,
    handleGroupClick,
    handleSectionClick,
    handleSearchDrawerToggle,
    handleClick,
    handleClose,
    handleSectionsDrawerToggle,
    handleSearchClick,
    handleSearchChange,
    handleScrollToTop,
    handleSearchEntriesChange,
    setValue,
    setAnchorEl,
    handleCloseDrawer,
    handlePublish,
    isSavingDraft,
    isResidental,
    isViewOnly,
    handleSaveDraft,
    handleShareDraft,
    handlePreviewDraft,
    setActiveGroup,
    setActiveSection,
    isProgrammaticScroll,
    setIsProgrammaticScroll,
  };
};
